import { db, auth } from '../config/firebase';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove, 
  setDoc, 
  serverTimestamp 
} from 'firebase/firestore';

export class CartService {
  static async getCart() {
    try {
      const cartRef = doc(db, 'carts', auth.currentUser.uid);
      const cartDoc = await getDoc(cartRef);
      
      if (!cartDoc.exists()) {
        await setDoc(cartRef, {
          items: [],
          updatedAt: serverTimestamp()
        });
        return { items: [], totalItems: 0 };
      }
      
      const items = cartDoc.data().items || [];
      return {
        items,
        totalItems: items.reduce((sum, item) => sum + (item.quantity || 0), 0)
      };
    } catch (error) {
      console.error('Error getting cart:', error);
      return { items: [], totalItems: 0 };
    }
  }

  static async addToCart(productId) {
    if (!auth.currentUser) throw new Error('User not authenticated');

    try {
      const cartRef = doc(db, 'carts', auth.currentUser.uid);
      const cartDoc = await getDoc(cartRef);
      const productDoc = await getDoc(doc(db, 'products', productId));

      if (!productDoc.exists()) {
        throw new Error('Product not found');
      }

      const productData = productDoc.data();
      console.log('Product data:', productData); // Debug log

      if (!productData.sellerId) {
        throw new Error('Invalid product data: missing sellerId');
      }

      const items = cartDoc.exists() ? (cartDoc.data()?.items || []) : [];

      const existingItemIndex = items.findIndex(item => item.productId === productId);

      if (existingItemIndex >= 0) {
        // Update quantity if item exists
        items[existingItemIndex].quantity = (items[existingItemIndex].quantity || 0) + 1;
      } else {
        // Add new item with complete product data including sellerId
        const newItem = {
          productId,
          quantity: 1,
          product: {
            id: productId,
            title: productData.title || '',
            price: productData.price || 0,
            image: productData.images?.[0] || null,
            sellerId: productData.sellerId
          }
        };
        console.log('New cart item:', newItem); // Debug log
        items.push(newItem);
      }

      await setDoc(cartRef, {
        items,
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  static async removeFromCart(productId) {
    try {
      const cartRef = doc(db, 'carts', auth.currentUser.uid);
      const cartDoc = await getDoc(cartRef);
      
      if (!cartDoc.exists()) return;

      const items = cartDoc.data().items.filter(
        item => item.productId !== productId
      );

      await updateDoc(cartRef, { items });
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  }

  static async clearCart() {
    try {
      const cartRef = doc(db, 'carts', auth.currentUser.uid);
      await updateDoc(cartRef, {
        items: []
      });
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  }

  static async updateQuantity(productId, quantity) {
    try {
      const cartRef = doc(db, 'carts', auth.currentUser.uid);
      const cartDoc = await getDoc(cartRef);
      
      if (!cartDoc.exists()) return;

      const items = cartDoc.data().items.map(item => 
        item.productId === productId 
          ? { ...item, quantity } 
          : item
      );

      await updateDoc(cartRef, { items });
    } catch (error) {
      console.error('Error updating quantity:', error);
      throw error;
    }
  }

  static async getProductDetails(productId) {
    try {
      const productDoc = await getDoc(doc(db, 'products', productId));
      if (!productDoc.exists()) {
        throw new Error('Product not found');
      }
      return {
        id: productDoc.id,
        ...productDoc.data()
      };
    } catch (error) {
      console.error('Error getting product details:', error);
      throw error;
    }
  }
}

export default CartService; 